/**
 * sl-u319.2.2: MarketplaceBrowseContent — Shared browse UI for Page and Dialog.
 *
 * Contains the full browse experience: type tabs, search, category filter,
 * sort, template grid, featured section, pagination, install/update overlays.
 *
 * Extracted from MarketplaceDialog to enable reuse in the Marketplace Page (sl-u319.2.1).
 */
import { useGuard } from '@affine/core/components/guard';
import {
  useConnectivityStatus,
  useUiOfflineSignal,
} from '@affine/core/components/hooks/use-connectivity-status';
import { useNavigateHelper } from '@affine/core/components/hooks/use-navigate-helper';
import { useLocalePicker } from '@affine/core/hooks/use-locale-picker';
import { AuthService } from '@affine/core/modules/cloud';
import { DocSwitcherService } from '@affine/core/modules/doc-switcher';
import {
  FEATURE_KEY_CUSTOM_APPS_CREATE,
  useFeatureGate,
} from '@affine/core/modules/streamlain/hooks/use-feature-gate';
import { ViewService } from '@affine/core/modules/workbench';
import { WorkspaceService } from '@affine/core/modules/workspace';
import { isBackendUnavailableError } from '@affine/core/utils/backend-availability';
import { useI18n } from '@affine/i18n';
import { useLiveData, useService } from '@toeverything/infra';
import type { ReactNode } from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { MarketplacePreviewGallery } from '../../pages/workspace/_shared/marketplace-preview-media';
import { PageBody } from '../../pages/workspace/_shared/page-body';
import {
  pageEmptySecondaryActionClass,
  PageEmptyState,
} from '../../pages/workspace/_shared/page-empty-state';
import { PageGridSkeleton } from '../../pages/workspace/_shared/page-grid-card';
import {
  PageDropdown,
  PageDropdownItem,
  PageDropdownLabel,
  PageFilterRow,
  PageHeaderGroup,
  PageHero,
  PageResultCount,
  PageSearch,
  PageSegment,
} from '../../pages/workspace/_shared/page-hero-filter';
import { PageContent } from '../../pages/workspace/_shared/page-shell';
import {
  MARKETPLACE_URL_FILTER_FALLBACK,
  marketplaceUrlFilterCodec,
  type MarketplaceUrlFilterState,
  type MarketplaceUrlInstallation,
  type MarketplaceUrlProvenance,
  type MarketplaceUrlSort,
} from '../../pages/workspace/_shared/page-url-filter-codecs';
import { usePageUrlFilterState } from '../../pages/workspace/_shared/use-page-url-filter-state';
import type { AutomationDatabaseBindings } from './automation-database-bindings';
import {
  AlertIcon,
  CheckIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DownloadIcon,
  EmptyBoxIcon,
  PlayIcon,
  SortIcon,
  StarIcon,
} from './icons';
import type { ConcretizationBindings } from './import-setup-wizard';
import { ImportSetupWizard, RatingDialog } from './import-setup-wizard';
import { resolveInstalledTemplateManagement } from './installed-template-management';
import {
  compareMarketplaceCategories,
  getMarketplaceCategoryLabel,
} from './marketplace-categories';
import * as styles from './styles.css';
import { formatCount, formatRating, TemplateCard } from './template-card';
import { isAgentTemplate, isCustomAppTemplateType } from './template-kind';
import { MarketplaceUpdateModal } from './update-modal';
import { useMarketplaceActions } from './use-marketplace-actions';
import type {
  BrowseFilter,
  InstalledTemplateInfo,
  MarketplaceBrowseTypeCounts,
  MarketplaceBrowseTypeTab,
  MarketplaceTemplate,
} from './use-marketplace-api';
import {
  deriveBrowseTypeFilter,
  isMarketplaceBrowseTypeAvailable,
  useInstalledTemplates,
  useMarketplaceBrowseTypeCounts,
  useMarketplaceCategories,
  useMarketplaceTemplateDetail,
  useMarketplaceTemplates,
} from './use-marketplace-api';

// =============================================================================
// Helpers (shared)
// =============================================================================

type InstallationFilter = MarketplaceUrlInstallation;
type ProvenanceFilter = MarketplaceUrlProvenance;
type BrowseTypeKey = MarketplaceBrowseTypeTab;
type BrowseSortKey = MarketplaceUrlSort;
type MarketplaceBrowseState = MarketplaceUrlFilterState;

function canRateMarketplaceTemplate(
  template: MarketplaceTemplate,
  currentUserId: string | null | undefined
): boolean {
  return (
    currentUserId != null &&
    template.status === 'APPROVED' &&
    template.publishedByUserId !== currentUserId
  );
}

function marketplaceCopy(
  t: ReturnType<typeof useI18n>,
  key: string,
  fallback: string
): string {
  const fn = (t as unknown as Record<string, unknown>)[key];
  if (typeof fn !== 'function') return fallback;
  return (fn as () => string)();
}

function getMarketplaceRatingBlockedMessage(
  template: MarketplaceTemplate,
  currentUserId: string | null | undefined,
  t: ReturnType<typeof useI18n>
): string | null {
  if (currentUserId == null) {
    return marketplaceCopy(
      t,
      'com.streamlain.marketplace.rating.blocked.sign-in',
      'Melde dich an, um diese Vorlage zu bewerten.'
    );
  }
  if (template.status !== 'APPROVED') {
    return marketplaceCopy(
      t,
      'com.streamlain.marketplace.rating.blocked.unapproved',
      'Diese Vorlage ist noch nicht freigegeben. Bewertungen sind nach der Marketplace-Freigabe möglich.'
    );
  }
  if (template.publishedByUserId === currentUserId) {
    return marketplaceCopy(
      t,
      'com.streamlain.marketplace.rating.blocked.own-template',
      'Du kannst eigene Vorlagen nicht bewerten. Öffne eine Vorlage eines anderen Publishers.'
    );
  }
  return null;
}

function resolveMarketplaceProvenance(
  template: MarketplaceTemplate
): Exclude<ProvenanceFilter, 'all'> {
  if (template.provenance) return template.provenance;
  if (template.publisher?.toLowerCase() === 'streamlain') {
    return 'streamlain_built';
  }
  if (template.verified) return 'streamlain_certified';
  return 'community_built';
}

function isTemplateInstalled(
  template: MarketplaceTemplate,
  installedByTemplateId: Map<string, InstalledTemplateInfo>
): boolean {
  if (template.templateType === 'app_block') return false;
  return (
    template.isPreinstalled === true ||
    installedByTemplateId.has(template.id) ||
    (template.installedVersionNumber ?? 0) > 0
  );
}

function MarketplaceDetailHero({
  template,
  ratingBlockedMessage,
  statusPill,
  semanticRow,
  onRate,
}: {
  template: MarketplaceTemplate;
  ratingBlockedMessage: string | null;
  statusPill?: ReactNode;
  semanticRow: ReactNode;
  onRate: (
    templateId: string,
    rating: number,
    comment?: string
  ) => Promise<unknown>;
}) {
  const t = useI18n();
  const picker = useLocalePicker();
  const [isRatingDialogOpen, setRatingDialogOpen] = useState(false);
  const [hasRated, setHasRated] = useState(false);

  useEffect(() => {
    setRatingDialogOpen(false);
    setHasRated(false);
  }, [template.id]);

  const title = picker?.pickTitle(template.titlesI18n) ?? '';
  const description = picker?.pickDescription(template.descriptionsI18n) ?? '';
  const typeKey =
    `com.streamlain.marketplace.card.type.${template.templateType}` as keyof typeof t;
  const typeLabel =
    typeof t[typeKey] === 'function'
      ? (t[typeKey] as () => string)()
      : template.templateType;
  const categoryLabel = getMarketplaceCategoryLabel(t, template.category);
  const downloads = template.downloadCount ?? 0;
  const rating = template.averageRating ?? 0;
  const executionCount = template.executionCount ?? 0;

  const metrics = (
    <div className={styles.detailMetricsRow}>
      <button
        type="button"
        className={styles.detailMetricsPill}
        onClick={() => setRatingDialogOpen(true)}
      >
        <span className={styles.detailMetric}>
          <DownloadIcon />
          <span className={styles.detailMetricValue}>
            {formatCount(downloads)}
          </span>
          <span className={styles.detailMetricLabel}>
            {t['com.streamlain.marketplace.browse.detail.metric.downloads']()}
          </span>
        </span>
        <span className={styles.detailMetricDivider} aria-hidden />
        <span className={styles.detailMetric}>
          <StarIcon />
          <span className={styles.detailMetricValue}>
            {formatRating(rating)}
          </span>
          <span className={styles.detailMetricLabel}>
            {t['com.streamlain.marketplace.browse.detail.metric.rating']()}
          </span>
        </span>
      </button>
      {executionCount > 0 && template.templateType !== 'step' ? (
        <span className={styles.detailMetric}>
          <PlayIcon />
          <span className={styles.detailMetricValue}>
            {formatCount(executionCount)}
          </span>
          <span className={styles.detailMetricLabel}>
            {t['com.streamlain.marketplace.browse.detail.metric.runs']()}
          </span>
        </span>
      ) : null}
      {statusPill}
    </div>
  );

  return (
    <>
      <PageHeaderGroup>
        <PageHero
          eyebrow={`${typeLabel} · ${categoryLabel}`}
          title={title}
          subtitle={description}
        >
          {metrics}
        </PageHero>
        {semanticRow}
      </PageHeaderGroup>
      <MarketplacePreviewGallery
        previewSet={template.previewSet}
        label={title}
      />
      <RatingDialog
        open={isRatingDialogOpen}
        templateId={template.id}
        averageRating={template.averageRating ?? 0}
        alreadyRated={hasRated}
        blockedMessage={ratingBlockedMessage}
        onClose={() => setRatingDialogOpen(false)}
        onSubmitted={() => setHasRated(true)}
        onRate={onRate}
      />
    </>
  );
}

function MarketplaceAppBlocksDetail({
  template,
  onBrowseAppBlocks,
}: {
  template: MarketplaceTemplate;
  onBrowseAppBlocks: () => void;
}) {
  const t = useI18n();
  if (
    template.templateType !== 'app' ||
    (template.appBlockIds?.length ?? 0) === 0
  ) {
    return null;
  }
  return (
    <PageFilterRow>
      <span className={styles.detailSectionTitle}>
        {t['com.streamlain.marketplace.browse.filter.app-block']()}
      </span>
      <span>
        {template.appBlockIds?.map(appBlockId => (
          <span className={styles.cardCategoryBadge} key={appBlockId}>
            {appBlockId}
          </span>
        ))}
      </span>
      <button
        className={styles.backButton}
        type="button"
        onClick={onBrowseAppBlocks}
      >
        {t['com.streamlain.marketplace.browse.filter.app-block']()}
      </button>
    </PageFilterRow>
  );
}

function applyMarketplaceClientFilters(
  templates: MarketplaceTemplate[],
  input: {
    expertAgentsAllowed: boolean;
    appsAllowed: boolean;
    installationFilter: InstallationFilter;
    provenanceFilter: ProvenanceFilter;
    installedByTemplateId: Map<string, InstalledTemplateInfo>;
  }
): MarketplaceTemplate[] {
  return templates.filter(template => {
    if (!input.expertAgentsAllowed && isAgentTemplate(template)) return false;
    if (!input.appsAllowed && isCustomAppTemplateType(template.templateType))
      return false;

    if (input.installationFilter !== 'all') {
      const installed = isTemplateInstalled(
        template,
        input.installedByTemplateId
      );
      if (input.installationFilter === 'installed' && !installed) return false;
      if (input.installationFilter === 'not-installed' && installed) {
        return false;
      }
    }

    if (
      input.provenanceFilter !== 'all' &&
      resolveMarketplaceProvenance(template) !== input.provenanceFilter
    ) {
      return false;
    }

    return true;
  });
}

// =============================================================================
// Overlay Types
// =============================================================================

type OverlayView =
  | null
  | {
      type: 'manage';
      template: MarketplaceTemplate;
      installedInfo?: InstalledTemplateInfo;
      focusRating?: boolean;
    }
  | { type: 'install'; template: MarketplaceTemplate; focusRating?: boolean }
  | {
      type: 'update';
      template: MarketplaceTemplate;
      installId: string;
      currentVersion: number;
      currentSemanticVersion: string | null;
      latestVersion: number;
      latestSemanticVersion: string | null;
      installedDocIds: string[];
    };

// =============================================================================
// Props
// =============================================================================

interface MarketplaceBrowseContentProps {
  /** Called when user navigates away (e.g. to an installed doc). Undefined in page mode. */
  onClose?: () => void;
  /** Initial category filter. */
  initialCategory?: string;
  /** Browse global marketplace or current workspace-private templates. */
  scope?: 'global' | 'workspace';
  /** Initial install filter for installed-focused tabs. */
  initialInstallationFilter?: InstallationFilter;
  /** Initial marketplace type tab for scoped page tabs such as Apps. */
  initialTypeTab?: BrowseTypeKey;
  /** Hide featured section in scoped/installed views. */
  showFeatured?: boolean;
  /** Called after successful install with templateId + type for visual feedback. */
  onInstallComplete?: (templateId: string, templateType: string) => void;
}

// =============================================================================
// Component
// =============================================================================

export function MarketplaceBrowseContent({
  onClose,
  initialCategory,
  scope = 'global',
  initialInstallationFilter = 'all',
  initialTypeTab,
  showFeatured = true,
  onInstallComplete,
}: MarketplaceBrowseContentProps) {
  const t = useI18n();
  const picker = useLocalePicker();
  const { isFeatureAllowed } = useFeatureGate();
  const authService = useService(AuthService);
  const currentAccount = useLiveData(authService.session.account$);
  const currentUserId = currentAccount?.id ?? null;
  const workspaceId = useService(WorkspaceService).workspace?.id;
  const canViewCustomApps = useGuard('Workspace_CustomApps_View') === true;
  const canCreateCustomApps = useGuard('Workspace_CustomApps_Create') === true;
  const installDisabled = !isFeatureAllowed('marketplace-install');
  const customAppsAllowed = isFeatureAllowed(FEATURE_KEY_CUSTOM_APPS_CREATE);
  const appsAllowed = customAppsAllowed && canViewCustomApps;
  const appInstallDisabled = !customAppsAllowed || !canCreateCustomApps;
  // Agent presets stay hidden until AI Agent has a distinct runtime contract.
  const expertAgentsAllowed = false;
  const usesUrlFilters = !onClose;
  const browseFallback = useMemo<MarketplaceBrowseState>(
    () =>
      usesUrlFilters
        ? MARKETPLACE_URL_FILTER_FALLBACK
        : {
            ...MARKETPLACE_URL_FILTER_FALLBACK,
            selectedCategory: initialCategory,
            templateTypeTab:
              initialTypeTab ?? MARKETPLACE_URL_FILTER_FALLBACK.templateTypeTab,
            installationFilter: initialInstallationFilter,
          },
    [initialCategory, initialInstallationFilter, initialTypeTab, usesUrlFilters]
  );
  const [urlBrowseState, setUrlBrowseState] = usePageUrlFilterState(
    marketplaceUrlFilterCodec
  );
  const [localBrowseState, setLocalBrowseState] =
    useState<MarketplaceBrowseState>(browseFallback);
  const browseState = usesUrlFilters ? urlBrowseState : localBrowseState;
  const updateBrowseState = useCallback(
    (
      patch: Partial<MarketplaceBrowseState>,
      options?: { replace?: boolean }
    ) => {
      if (usesUrlFilters) {
        setUrlBrowseState(previous => ({ ...previous, ...patch }), options);
        return;
      }
      setLocalBrowseState(previous => ({ ...previous, ...patch }));
    },
    [setUrlBrowseState, usesUrlFilters]
  );

  const {
    search,
    templateTypeTab,
    selectedCategory,
    currentPage,
    sortBy,
    installationFilter,
    provenanceFilter,
  } = browseState;

  useEffect(() => {
    if (!expertAgentsAllowed && templateTypeTab === 'agent') {
      updateBrowseState(
        { templateTypeTab: 'all', currentPage: 1 },
        { replace: true }
      );
    }
    if (
      !appsAllowed &&
      (templateTypeTab === 'app' || templateTypeTab === 'app_block')
    ) {
      updateBrowseState(
        { templateTypeTab: 'all', currentPage: 1 },
        { replace: true }
      );
    }
  }, [appsAllowed, expertAgentsAllowed, templateTypeTab, updateBrowseState]);

  const [debouncedSearch, setDebouncedSearch] = useState(search);
  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timeout);
  }, [search]);

  // Overlay navigation
  const [overlay, setOverlay] = useState<OverlayView>(null);
  const [detailSearchParams] = useSearchParams();
  const detailTemplateId = usesUrlFilters
    ? detailSearchParams.get('detail')
    : null;
  const deepLinkedTemplate = useMarketplaceTemplateDetail(
    workspaceId,
    detailTemplateId
  );
  const serializedDetailSearchParams = detailSearchParams.toString();
  const restoredDetailIdRef = useRef<string | null>(null);
  const docSwitcher = useService(DocSwitcherService);
  const view = useService(ViewService).view;
  const closeOverlay = useCallback(() => {
    if (usesUrlFilters) {
      const next = new URLSearchParams(detailSearchParams);
      const closingDetailId = next.get('detail');
      if (closingDetailId) {
        restoredDetailIdRef.current = closingDetailId;
        view.updateQueryString<{ detail?: string }>(
          { detail: undefined },
          { replace: true }
        );
      }
    }
    setOverlay(null);
  }, [detailSearchParams, usesUrlFilters, view]);

  const handleSearchChange = useCallback(
    (value: string) => {
      updateBrowseState({ search: value, currentPage: 1 }, { replace: true });
    },
    [updateBrowseState]
  );

  const handleTypeChange = useCallback(
    (key: BrowseTypeKey) => {
      updateBrowseState({ templateTypeTab: key, currentPage: 1 });
    },
    [updateBrowseState]
  );
  const handleBrowseAppBlocksFromDetail = useCallback(() => {
    closeOverlay();
    setDebouncedSearch('');
    updateBrowseState({
      search: '',
      selectedCategory: undefined,
      templateTypeTab: 'app_block',
      currentPage: 1,
    });
  }, [closeOverlay, updateBrowseState]);

  const handleCategoryChange = useCallback(
    (category: string | undefined) => {
      updateBrowseState({ selectedCategory: category, currentPage: 1 });
    },
    [updateBrowseState]
  );

  const handleSortChange = useCallback(
    (sort: BrowseSortKey) => {
      updateBrowseState({ sortBy: sort, currentPage: 1 });
    },
    [updateBrowseState]
  );

  const handleInstallationFilterChange = useCallback(
    (filter: InstallationFilter) => {
      updateBrowseState({ installationFilter: filter, currentPage: 1 });
    },
    [updateBrowseState]
  );

  const handleProvenanceFilterChange = useCallback(
    (filter: ProvenanceFilter) => {
      updateBrowseState({ provenanceFilter: filter, currentPage: 1 });
    },
    [updateBrowseState]
  );

  const resetBrowseFilters = useCallback(() => {
    if (usesUrlFilters) {
      setUrlBrowseState(MARKETPLACE_URL_FILTER_FALLBACK);
      return;
    }
    setLocalBrowseState(MARKETPLACE_URL_FILTER_FALLBACK);
  }, [setUrlBrowseState, usesUrlFilters]);
  const resetBrowseFacetFilters = useCallback(() => {
    updateBrowseState({
      selectedCategory: undefined,
      installationFilter: 'all',
      provenanceFilter: 'all',
      currentPage: 1,
    });
  }, [updateBrowseState]);

  // API data
  const filter = useMemo<BrowseFilter>(
    () => ({
      search: debouncedSearch || undefined,
      category: selectedCategory,
      page: currentPage,
      limit: 20,
      sortBy,
      ...deriveBrowseTypeFilter(templateTypeTab),
    }),
    [debouncedSearch, selectedCategory, currentPage, sortBy, templateTypeTab]
  );
  const featuredFilter = useMemo<BrowseFilter>(
    () => ({
      featured: true,
      limit: 10,
      sortBy: 'type',
      ...deriveBrowseTypeFilter(templateTypeTab),
    }),
    [templateTypeTab]
  );

  const { templates, total, page, isLoading, error } = useMarketplaceTemplates(
    filter,
    {
      workspaceId: scope === 'workspace' ? (workspaceId ?? null) : null,
    }
  );
  const { templates: featuredTemplates } = useMarketplaceTemplates(
    featuredFilter,
    {
      workspaceId: scope === 'workspace' ? (workspaceId ?? null) : null,
    }
  );
  const { categories, isLoading: categoriesLoading } = useMarketplaceCategories(
    {
      workspaceId: scope === 'workspace' ? (workspaceId ?? null) : null,
    }
  );
  const { counts: marketplaceTypeCounts } = useMarketplaceBrowseTypeCounts({
    workspaceId: scope === 'workspace' ? (workspaceId ?? null) : null,
  });

  // sl-mkx.1.3: Lookup for installed templates in the current workspace.
  const { byTemplateId: installedByTemplateId, refetch: refetchInstalled } =
    useInstalledTemplates(workspaceId ?? null);
  const connectivityStatus = useConnectivityStatus();
  const localBackendUnavailable = useUiOfflineSignal(
    isBackendUnavailableError(error)
  );
  const isBackendUnavailable =
    connectivityStatus.offline || localBackendUnavailable;

  // Actions
  const {
    installTemplate,
    installStep,
    installResult,
    installError,
    resetInstall,
    rateTemplate,
  } = useMarketplaceActions(workspaceId);

  const visibleTemplates = useMemo(
    () =>
      applyMarketplaceClientFilters(templates, {
        expertAgentsAllowed,
        appsAllowed,
        installationFilter,
        provenanceFilter,
        installedByTemplateId,
      }),
    [
      expertAgentsAllowed,
      appsAllowed,
      installationFilter,
      installedByTemplateId,
      provenanceFilter,
      templates,
    ]
  );
  const visibleFeaturedTemplates = useMemo(
    () =>
      applyMarketplaceClientFilters(featuredTemplates, {
        expertAgentsAllowed,
        appsAllowed,
        installationFilter,
        provenanceFilter,
        installedByTemplateId,
      }),
    [
      expertAgentsAllowed,
      appsAllowed,
      featuredTemplates,
      installationFilter,
      installedByTemplateId,
      provenanceFilter,
    ]
  );
  useEffect(() => {
    if (!detailTemplateId || overlay) return;
    if (restoredDetailIdRef.current === detailTemplateId) return;
    const template =
      [...templates, ...featuredTemplates].find(
        candidate => candidate.id === detailTemplateId
      ) ??
      (deepLinkedTemplate?.id === detailTemplateId ? deepLinkedTemplate : null);
    if (!template) return;
    if (!appsAllowed && isCustomAppTemplateType(template.templateType)) {
      restoredDetailIdRef.current = detailTemplateId;
      view.updateQueryString<{ detail?: string }>(
        { detail: undefined },
        { replace: true }
      );
      return;
    }
    restoredDetailIdRef.current = detailTemplateId;
    const installedInfo = installedByTemplateId.get(template.id);
    setOverlay(
      installedInfo
        ? { type: 'manage', template, installedInfo }
        : { type: 'install', template }
    );
  }, [
    detailTemplateId,
    deepLinkedTemplate,
    appsAllowed,
    detailSearchParams,
    featuredTemplates,
    installedByTemplateId,
    overlay,
    templates,
    view,
  ]);
  useEffect(() => {
    if (!usesUrlFilters) return;
    const template = overlay?.template;
    const next = new URLSearchParams(detailSearchParams);
    if (template) {
      restoredDetailIdRef.current = template.id;
      if (next.get('detail') !== template.id) {
        view.updateQueryString({ detail: template.id }, { replace: true });
      }
      return;
    }
  }, [detailSearchParams, overlay, usesUrlFilters, view]);
  useEffect(() => {
    if (!usesUrlFilters) return;
    docSwitcher.updateDestination('hub', {
      title: overlay?.template
        ? picker?.pickTitle(overlay.template.titlesI18n) || undefined
        : undefined,
      to: `/marketplace${
        serializedDetailSearchParams ? `?${serializedDetailSearchParams}` : ''
      }`,
    });
  }, [
    docSwitcher,
    overlay,
    picker,
    serializedDetailSearchParams,
    usesUrlFilters,
  ]);
  const hasClientFilters =
    installationFilter !== 'all' ||
    provenanceFilter !== 'all' ||
    (!expertAgentsAllowed && templateTypeTab === 'all') ||
    (!appsAllowed && templateTypeTab === 'all');
  const availableTypeCounts =
    useMemo<MarketplaceBrowseTypeCounts | null>(() => {
      if (!marketplaceTypeCounts) return null;
      const gatedCounts = appsAllowed
        ? marketplaceTypeCounts
        : {
            ...marketplaceTypeCounts,
            app: 0,
            app_block: 0,
          };
      return gatedCounts;
    }, [appsAllowed, marketplaceTypeCounts]);
  const visibleCategories = useMemo(
    () => categories.filter(category => category.count > 0),
    [categories]
  );
  useEffect(() => {
    if (
      isMarketplaceBrowseTypeAvailable(templateTypeTab, availableTypeCounts, {
        expertAgentsAllowed,
        appsAllowed,
      })
    ) {
      return;
    }
    updateBrowseState(
      { templateTypeTab: 'all', currentPage: 1 },
      { replace: true }
    );
  }, [
    availableTypeCounts,
    appsAllowed,
    expertAgentsAllowed,
    templateTypeTab,
    updateBrowseState,
  ]);
  useEffect(() => {
    if (categoriesLoading || !selectedCategory) return;
    if (
      visibleCategories.some(category => category.name === selectedCategory)
    ) {
      return;
    }
    updateBrowseState(
      { selectedCategory: undefined, currentPage: 1 },
      { replace: true }
    );
  }, [
    categoriesLoading,
    selectedCategory,
    updateBrowseState,
    visibleCategories,
  ]);
  const visibleTotal = hasClientFilters ? visibleTemplates.length : total;
  const visiblePageCount = visibleTemplates.length;
  const paginatedTemplateTotal = hasClientFilters
    ? visibleTemplates.length
    : total;
  const totalPages = Math.max(1, Math.ceil(paginatedTemplateTotal / 20));
  const hasActiveBrowseFilters =
    search.length > 0 ||
    templateTypeTab !== 'all' ||
    selectedCategory !== undefined ||
    sortBy !== 'type' ||
    installationFilter !== 'all' ||
    provenanceFilter !== 'all' ||
    currentPage > 1;

  // sl-mkx.3.4: single install path — the ImportSetupWizard handles the
  // target-doc + DB-options steps for automation templates internally, so
  // the AutomationInstallDialog pre-step is gone.
  const handleTemplateOpen = useCallback(
    (template: MarketplaceTemplate, options?: { focusRating?: boolean }) => {
      if (!appsAllowed && isCustomAppTemplateType(template.templateType)) {
        return;
      }
      const installedInfo =
        template.templateType === 'app_block'
          ? undefined
          : installedByTemplateId.get(template.id);
      // Route to manage only when we have a concrete install record. For
      // preinstalled/system templates (no install record but isPreinstalled
      // or legacyInstalled flag) we still open the install/preview wizard so
      // the user reaches a detail page — otherwise the card click would
      // resolve to a 'manage' overlay that fails its render guard and looks
      // dead. Step templates hit this path most often (system steps).
      setOverlay(
        installedInfo
          ? {
              type: 'manage',
              template,
              installedInfo,
              focusRating: options?.focusRating,
            }
          : { type: 'install', template, focusRating: options?.focusRating }
      );
    },
    [appsAllowed, installedByTemplateId]
  );

  const isTemplateInstallDisabled = useCallback(
    (template: MarketplaceTemplate) =>
      installDisabled ||
      (isCustomAppTemplateType(template.templateType) && appInstallDisabled),
    [appInstallDisabled, installDisabled]
  );

  const handleInstallConfirm = useCallback(
    (template: MarketplaceTemplate) => {
      if (isTemplateInstallDisabled(template)) return;
      setOverlay({ type: 'install', template });
    },
    [isTemplateInstallDisabled]
  );

  // sl-mkx.2.3: open the dedicated update modal for an installed template
  const handleUpdateRequest = useCallback(
    (template: MarketplaceTemplate) => {
      const info = installedByTemplateId.get(template.id);
      if (!info) return;
      setOverlay({
        type: 'update',
        template,
        installId: info.installId,
        currentVersion: info.versionNumber,
        currentSemanticVersion: info.semanticVersion,
        latestVersion: info.latestVersion,
        latestSemanticVersion: info.latestSemanticVersion,
        installedDocIds: info.installedDocIds,
      });
    },
    [installedByTemplateId]
  );

  const handleInstallExecute = useCallback(
    (
      templateId: string,
      target: 'new-doc' | 'stay' = 'new-doc',
      bindings?: ConcretizationBindings,
      installOptions?: {
        targetVersionNumber?: number;
        targetDocId?: string;
        createInputDbs?: boolean;
        createOutputDbs?: boolean;
        databaseBindings?: AutomationDatabaseBindings;
        appBlockTarget?:
          | {
              operationId: string;
              kind: 'existing_app';
              customAppId: string;
            }
          | {
              operationId: string;
              kind: 'new_app';
              title: string;
              appKind: 'workspace';
            };
      },
      templateSummary?: MarketplaceTemplate
    ) => {
      installTemplate(
        templateId,
        target,
        bindings,
        installOptions,
        templateSummary
      ).catch(() => {
        // errors are handled in hook state
      });
    },
    [installTemplate]
  );

  const { openPage, jumpToPageBlock, jumpToWorkflows, jumpToCustomApps } =
    useNavigateHelper();
  const openTemplateDoc = useCallback(
    (template: MarketplaceTemplate, docId: string) => {
      if (!workspaceId) return;
      onClose?.();
      if (template.templateType === 'automation') {
        jumpToPageBlock(workspaceId, docId, 'edgeless');
        return;
      }
      openPage(workspaceId, docId);
    },
    [jumpToPageBlock, onClose, openPage, workspaceId]
  );

  const openInstalledTemplate = useCallback(
    (template: MarketplaceTemplate, installedInfo?: InstalledTemplateInfo) => {
      if (!workspaceId) return;
      const management = resolveInstalledTemplateManagement(
        template,
        installedInfo
      );
      const title =
        picker?.pickTitle(template.titlesI18n) ??
        installedInfo?.templateTitle ??
        '';

      if (management.primaryAction === 'open_apps') {
        onClose?.();
        jumpToCustomApps(workspaceId);
        return;
      }

      if (management.canOpenDoc && management.primaryDocId) {
        openTemplateDoc(template, management.primaryDocId);
        return;
      }

      if (management.canOpenWorkflow && management.workflowId) {
        onClose?.();
        jumpToWorkflows(workspaceId, {
          id: management.workflowId,
          name: title,
        });
        return;
      }

      if (management.canUseStep) {
        onClose?.();
        jumpToWorkflows(workspaceId);
      }
    },
    [
      jumpToCustomApps,
      jumpToWorkflows,
      onClose,
      openTemplateDoc,
      picker,
      workspaceId,
    ]
  );

  const openInstalledWorkflow = useCallback(
    (template: MarketplaceTemplate, installedInfo?: InstalledTemplateInfo) => {
      if (!workspaceId) return;
      const management = resolveInstalledTemplateManagement(
        template,
        installedInfo
      );
      if (!management.workflowId) return;
      const title =
        picker?.pickTitle(template.titlesI18n) ??
        installedInfo?.templateTitle ??
        '';
      onClose?.();
      jumpToWorkflows(workspaceId, {
        id: management.workflowId,
        name: title,
      });
    },
    [jumpToWorkflows, onClose, picker, workspaceId]
  );

  const finishInstallState = useCallback(() => {
    // Notify parent of install completion for visual feedback
    if (installResult && overlay?.type === 'install') {
      onInstallComplete?.(
        overlay.template.id,
        overlay.template.templateType ?? 'workflow'
      );
    }

    // sl-mkx.1.3: refresh the installed lookup so cards flip to the correct
    // state without a page reload.
    void refetchInstalled();

    resetInstall();
    closeOverlay();
  }, [
    closeOverlay,
    resetInstall,
    installResult,
    overlay,
    onInstallComplete,
    refetchInstalled,
  ]);

  const handleInstallBackToMarketplace = useCallback(() => {
    finishInstallState();
  }, [finishInstallState]);

  const handleInstallOpen = useCallback(() => {
    if (!workspaceId || overlay?.type !== 'install') {
      finishInstallState();
      return;
    }
    const template = overlay.template;
    const primaryDocId = installResult?.installedDocIds?.[0];
    const workflowId = installResult?.workflowId;
    finishInstallState();
    if (template.templateType === 'app_block') {
      onClose?.();
      jumpToCustomApps(workspaceId);
      return;
    }
    if (primaryDocId) {
      openTemplateDoc(template, primaryDocId);
      return;
    }
    if (workflowId) {
      const title = picker?.pickTitle(template.titlesI18n) ?? '';
      onClose?.();
      jumpToWorkflows(workspaceId, { id: workflowId, name: title });
      return;
    }
    if (template.templateType === 'step') {
      onClose?.();
      jumpToWorkflows(workspaceId);
    }
  }, [
    finishInstallState,
    installResult?.installedDocIds,
    installResult?.workflowId,
    jumpToCustomApps,
    jumpToWorkflows,
    onClose,
    openTemplateDoc,
    overlay,
    picker,
    workspaceId,
  ]);

  const totalAcrossFilter = visibleTotal;
  const featuredCount = visibleFeaturedTemplates.length;
  const [detailActionPortal, setDetailActionPortal] =
    useState<HTMLDivElement | null>(null);
  const [marketplaceOverlayPortal, setMarketplaceOverlayPortal] =
    useState<HTMLDivElement | null>(null);
  const detailPage =
    overlay?.type === 'manage' && overlay.installedInfo && workspaceId ? (
      <>
        <MarketplaceDetailHero
          template={overlay.template}
          ratingBlockedMessage={getMarketplaceRatingBlockedMessage(
            overlay.template,
            currentUserId,
            t
          )}
          statusPill={
            <span className={styles.manageStatusPill}>
              <CheckIcon size={12} />
              {resolveInstalledTemplateManagement(
                overlay.template,
                overlay.installedInfo
              ).isPreinstalled
                ? t[
                    'com.streamlain.marketplace.installed.status.preinstalled'
                  ]()
                : t['com.streamlain.marketplace.installed.status.installed']()}
            </span>
          }
          semanticRow={
            <>
              <MarketplaceAppBlocksDetail
                template={overlay.template}
                onBrowseAppBlocks={handleBrowseAppBlocksFromDetail}
              />
              <PageFilterRow>
                <span className={styles.detailBackRowTitle}>
                  {t['com.streamlain.marketplace.installed.manage.title']()}
                </span>
              </PageFilterRow>
            </>
          }
          onRate={rateTemplate}
        />
        <PageBody>
          <ImportSetupWizard
            mode="manage"
            chrome="page"
            template={overlay.template}
            workspaceId={workspaceId}
            canRate={canRateMarketplaceTemplate(
              overlay.template,
              currentUserId
            )}
            autoFocusRating={overlay.focusRating === true}
            installedInfo={overlay.installedInfo}
            management={resolveInstalledTemplateManagement(
              overlay.template,
              overlay.installedInfo
            )}
            onBack={closeOverlay}
            onCancel={closeOverlay}
            onOpen={() =>
              openInstalledTemplate(overlay.template, overlay.installedInfo)
            }
            onOpenWorkflow={() =>
              openInstalledWorkflow(overlay.template, overlay.installedInfo)
            }
            onAddAnother={() =>
              setOverlay({ type: 'install', template: overlay.template })
            }
            onUpdate={() => handleUpdateRequest(overlay.template)}
            onRate={rateTemplate}
            actionPortal={detailActionPortal}
          />
        </PageBody>
        <div ref={setDetailActionPortal} />
      </>
    ) : overlay?.type === 'install' && workspaceId ? (
      <>
        <MarketplaceDetailHero
          template={overlay.template}
          ratingBlockedMessage={getMarketplaceRatingBlockedMessage(
            overlay.template,
            currentUserId,
            t
          )}
          semanticRow={
            <>
              <MarketplaceAppBlocksDetail
                template={overlay.template}
                onBrowseAppBlocks={handleBrowseAppBlocksFromDetail}
              />
              <PageFilterRow>
                <span className={styles.detailBackRowTitle}>
                  {t['com.streamlain.marketplace.import.title']()}
                </span>
              </PageFilterRow>
            </>
          }
          onRate={rateTemplate}
        />
        <PageBody>
          <ImportSetupWizard
            chrome="page"
            template={overlay.template}
            workspaceId={workspaceId}
            canRate={canRateMarketplaceTemplate(
              overlay.template,
              currentUserId
            )}
            autoFocusRating={overlay.focusRating === true}
            onInstall={async (bindings, options) => {
              // sl-mkx.3.2: install options carried through to the execute
              // path; 'new-doc' vs 'stay' keeps the doc-open routing that
              // predates the wizard-step rework.
              const target = options.targetDocId ? 'stay' : 'new-doc';
              handleInstallExecute(
                overlay.template.id,
                target,
                bindings,
                options,
                overlay.template
              );
            }}
            onRate={rateTemplate}
            onBack={closeOverlay}
            onCancel={() => {
              resetInstall();
              closeOverlay();
            }}
            isInstalling={
              installStep !== null &&
              installStep !== 'done' &&
              installStep !== 'error'
            }
            installStep={installStep}
            installResult={installResult}
            installError={installError}
            onInstallOpen={handleInstallOpen}
            onInstallBackToMarketplace={handleInstallBackToMarketplace}
            onInstallReset={resetInstall}
            actionPortal={detailActionPortal}
            overlayPortal={marketplaceOverlayPortal}
          />
        </PageBody>
        <div ref={setDetailActionPortal} />
      </>
    ) : null;

  return (
    <PageContent
      className={styles.dialogContent}
      style={{ position: 'relative', flex: 1, overflow: 'hidden' }}
    >
      {detailPage ?? (
        <>
          <PageHeaderGroup>
            <BrowseHero
              total={totalAcrossFilter}
              featured={featuredCount}
              activeType={templateTypeTab}
              hideStats={Boolean(error && isBackendUnavailable)}
              scope={scope}
            />
            <BrowseFilterRow
              searchValue={search}
              onSearchChange={handleSearchChange}
              activeType={templateTypeTab}
              onTypeChange={handleTypeChange}
              typeCounts={availableTypeCounts}
              sortBy={sortBy}
              onSortChange={handleSortChange}
              showSort={visibleTotal > 3}
              categories={visibleCategories}
              selectedCategory={selectedCategory}
              onCategoryChange={handleCategoryChange}
              installationFilter={installationFilter}
              onInstallationFilterChange={handleInstallationFilterChange}
              provenanceFilter={provenanceFilter}
              onProvenanceFilterChange={handleProvenanceFilterChange}
              onResetFacetFilters={resetBrowseFacetFilters}
              resultCountText={
                !isLoading && !error && !hasClientFilters
                  ? t['com.streamlain.quicksearch.advanced.results']({
                      count: formatCount(visiblePageCount),
                      total: formatCount(visibleTotal),
                    })
                  : undefined
              }
              showAgents={expertAgentsAllowed}
              showApps={appsAllowed}
            />
          </PageHeaderGroup>

          <PageBody>
            {isLoading && (
              <PageGridSkeleton count={6} testId="marketplace-skeleton" />
            )}

            {error && !isLoading && (
              <PageEmptyState
                icon={<AlertIcon />}
                title={
                  isBackendUnavailable
                    ? t[
                        'com.streamlain.marketplace.browse.state.unavailable.title'
                      ]()
                    : t['com.streamlain.marketplace.browse.state.error.title']()
                }
                hint={
                  isBackendUnavailable
                    ? t[
                        'com.streamlain.marketplace.browse.state.unavailable.hint'
                      ]()
                    : error
                }
              />
            )}

            {!isLoading && !error && visibleTemplates.length === 0 && (
              <PageEmptyState
                icon={<EmptyBoxIcon />}
                title={t[
                  'com.streamlain.marketplace.browse.state.empty.title'
                ]()}
                hint={t['com.streamlain.marketplace.browse.state.empty.hint']()}
                action={
                  hasActiveBrowseFilters ? (
                    <button
                      type="button"
                      className={pageEmptySecondaryActionClass}
                      onClick={resetBrowseFilters}
                      data-testid="marketplace-reset-filters"
                    >
                      {t['com.streamlain.marketplace.browse.filter.reset']()}
                    </button>
                  ) : undefined
                }
              />
            )}

            {!isLoading &&
              showFeatured &&
              !debouncedSearch &&
              !selectedCategory &&
              installationFilter === 'all' &&
              provenanceFilter === 'all' &&
              visibleFeaturedTemplates.length > 0 && (
                <>
                  <SectionHeader
                    label={t[
                      'com.streamlain.marketplace.browse.section.featured'
                    ]()}
                    featured
                  />
                  <div className={styles.cardGrid}>
                    {visibleFeaturedTemplates.map(tpl => (
                      <TemplateCard
                        key={`featured-${tpl.id}`}
                        template={tpl}
                        assetWorkspaceId={
                          scope === 'workspace' ? (workspaceId ?? null) : null
                        }
                        featured
                        installDisabled={isTemplateInstallDisabled(tpl)}
                        installedInfo={installedByTemplateId.get(tpl.id)}
                        onSelect={() => handleTemplateOpen(tpl)}
                        onInstall={() => handleInstallConfirm(tpl)}
                        onUpdate={() => handleUpdateRequest(tpl)}
                      />
                    ))}
                  </div>
                  {visibleTemplates.length > 0 && (
                    <SectionHeader
                      label={t[
                        'com.streamlain.marketplace.browse.section.community'
                      ]()}
                    />
                  )}
                </>
              )}

            {!isLoading && !error && visibleTemplates.length > 0 && (
              <div className={styles.cardGrid}>
                {visibleTemplates.map(tpl => (
                  <TemplateCard
                    key={tpl.id}
                    template={tpl}
                    assetWorkspaceId={
                      scope === 'workspace' ? (workspaceId ?? null) : null
                    }
                    installDisabled={isTemplateInstallDisabled(tpl)}
                    installedInfo={installedByTemplateId.get(tpl.id)}
                    onSelect={() => handleTemplateOpen(tpl)}
                    onInstall={() => handleInstallConfirm(tpl)}
                    onUpdate={() => handleUpdateRequest(tpl)}
                  />
                ))}
              </div>
            )}
          </PageBody>

          {paginatedTemplateTotal > 20 && (
            <div className={styles.pagination}>
              <button
                type="button"
                className={styles.paginationButton}
                disabled={page <= 1}
                onClick={() =>
                  updateBrowseState({
                    currentPage: Math.max(1, currentPage - 1),
                  })
                }
                aria-label="Previous page"
              >
                <ChevronLeftIcon size={14} />
                {t['com.streamlain.marketplace.browse.pagination.prev']()}
              </button>
              <span className={styles.paginationInfo}>
                {t['com.streamlain.marketplace.browse.pagination.info']({
                  page: String(page),
                  total: String(totalPages),
                  count: formatCount(paginatedTemplateTotal),
                })}
              </span>
              <button
                type="button"
                className={styles.paginationButton}
                disabled={page >= totalPages}
                onClick={() =>
                  updateBrowseState({ currentPage: currentPage + 1 })
                }
                aria-label="Next page"
              >
                {t['com.streamlain.marketplace.browse.pagination.next']()}
                <ChevronRightIcon size={14} />
              </button>
            </div>
          )}
        </>
      )}

      {/* sl-mkx.2.3: Update overlay — in-place version bump with changelog review */}
      {overlay?.type === 'update' && workspaceId && (
        <MarketplaceUpdateModal
          installId={overlay.installId}
          template={overlay.template}
          templateId={overlay.template.id}
          templateTitle={picker?.pickTitle(overlay.template.titlesI18n) ?? ''}
          workspaceId={workspaceId}
          currentVersion={overlay.currentVersion}
          currentSemanticVersion={overlay.currentSemanticVersion}
          latestVersion={overlay.latestVersion}
          latestSemanticVersion={overlay.latestSemanticVersion}
          installedDocIds={overlay.installedDocIds}
          onClose={closeOverlay}
          onUpdated={() => {
            void refetchInstalled();
            closeOverlay();
          }}
        />
      )}
      <div ref={setMarketplaceOverlayPortal} />
    </PageContent>
  );
}

// =============================================================================
// Sub-components (local to this module)
// =============================================================================

function BrowseFilterRow({
  searchValue,
  onSearchChange,
  activeType,
  onTypeChange,
  typeCounts,
  sortBy,
  onSortChange,
  showSort,
  categories,
  selectedCategory,
  onCategoryChange,
  installationFilter,
  onInstallationFilterChange,
  provenanceFilter,
  onProvenanceFilterChange,
  onResetFacetFilters,
  resultCountText,
  showAgents,
  showApps,
}: {
  searchValue: string;
  onSearchChange: (value: string) => void;
  activeType: BrowseTypeKey;
  onTypeChange: (key: BrowseTypeKey) => void;
  typeCounts: MarketplaceBrowseTypeCounts | null;
  sortBy: BrowseSortKey;
  onSortChange: (sort: BrowseSortKey) => void;
  showSort: boolean;
  categories: Array<{ name: string; count: number }>;
  selectedCategory: string | undefined;
  onCategoryChange: (cat: string | undefined) => void;
  installationFilter: InstallationFilter;
  onInstallationFilterChange: (filter: InstallationFilter) => void;
  provenanceFilter: ProvenanceFilter;
  onProvenanceFilterChange: (filter: ProvenanceFilter) => void;
  onResetFacetFilters: () => void;
  resultCountText?: string;
  showAgents: boolean;
  showApps: boolean;
}) {
  const t = useI18n();

  const installationLabels: Record<InstallationFilter, string> = {
    all: t['com.streamlain.marketplace.browse.filter.all'](),
    installed: t['com.streamlain.marketplace.browse.filter.installed'](),
    'not-installed':
      t['com.streamlain.marketplace.browse.filter.not-installed'](),
  };
  const provenanceLabels: Record<ProvenanceFilter, string> = {
    all: t['com.streamlain.marketplace.browse.filter.all'](),
    streamlain_built:
      t['com.streamlain.marketplace.card.trust.streamlain-built'](),
    streamlain_certified:
      t['com.streamlain.marketplace.card.trust.streamlain-certified'](),
    community_built:
      t['com.streamlain.marketplace.card.trust.community-built'](),
  };
  const activeFilterCount = [
    selectedCategory,
    installationFilter !== 'all' ? installationFilter : null,
    provenanceFilter !== 'all' ? provenanceFilter : null,
  ].filter(Boolean).length;
  const sortedCategories = useMemo(
    () =>
      [...categories].sort((a, b) =>
        compareMarketplaceCategories(a.name, b.name)
      ),
    [categories]
  );

  const typeOptions = (
    [
      {
        key: 'all' as BrowseTypeKey,
        label: t['com.streamlain.marketplace.browse.filter.all'](),
      },
      {
        key: 'agent' as BrowseTypeKey,
        label: t['com.streamlain.marketplace.browse.filter.agent'](),
      },
      {
        key: 'app' as BrowseTypeKey,
        label: t['com.streamlain.marketplace.browse.filter.app'](),
      },
      {
        key: 'app_block' as BrowseTypeKey,
        label: t['com.streamlain.marketplace.browse.filter.app-block'](),
      },
      {
        key: 'doc' as BrowseTypeKey,
        label: t['com.streamlain.marketplace.browse.filter.doc'](),
      },
      {
        key: 'automation' as BrowseTypeKey,
        label: t['com.streamlain.marketplace.browse.filter.automation'](),
      },
      {
        key: 'workflow' as BrowseTypeKey,
        label: t['com.streamlain.marketplace.browse.filter.workflow'](),
      },
      {
        key: 'step' as BrowseTypeKey,
        label: t['com.streamlain.marketplace.browse.filter.step.short'](),
      },
    ] as const
  ).filter(option =>
    isMarketplaceBrowseTypeAvailable(option.key, typeCounts, {
      expertAgentsAllowed: showAgents,
      appsAllowed: showApps,
    })
  );

  const sortOptions: Array<{ key: BrowseSortKey; labelKey: string }> = [
    {
      key: 'type',
      labelKey: 'com.streamlain.marketplace.browse.sort.type',
    },
    {
      key: 'downloads',
      labelKey: 'com.streamlain.marketplace.browse.sort.downloads',
    },
    {
      key: 'rating',
      labelKey: 'com.streamlain.marketplace.browse.sort.rating',
    },
    {
      key: 'newest',
      labelKey: 'com.streamlain.marketplace.browse.sort.newest',
    },
  ];

  const activeSortLabel = (
    sortOptions.find(o => o.key === sortBy) ?? sortOptions[0]
  ).labelKey;

  return (
    <PageFilterRow>
      <PageSearch
        value={searchValue}
        onChange={onSearchChange}
        placeholder={t[
          'com.streamlain.marketplace.browse.search.placeholder'
        ]()}
      />

      <PageSegment
        options={typeOptions}
        active={activeType}
        onChange={onTypeChange}
      />

      <PageDropdown
        active={activeFilterCount > 0}
        trigger={
          <>
            <PageDropdownLabel>
              {t['com.streamlain.marketplace.browse.filter.label']()}
            </PageDropdownLabel>
            {activeFilterCount > 0
              ? t['com.streamlain.marketplace.browse.filter.active-count']({
                  count: String(activeFilterCount),
                })
              : t['com.streamlain.marketplace.browse.filter.all']()}
          </>
        }
      >
        {({ close }) => (
          <>
            <span className={styles.filterMenuSection}>
              {t['com.streamlain.marketplace.browse.filter.category']()}
            </span>
            <PageDropdownItem
              selected={!selectedCategory}
              onClick={() => {
                onCategoryChange(undefined);
              }}
            >
              {t['com.streamlain.marketplace.browse.category.all']()}
            </PageDropdownItem>
            {sortedCategories.map(cat => (
              <PageDropdownItem
                key={cat.name}
                selected={selectedCategory === cat.name}
                onClick={() => {
                  onCategoryChange(cat.name);
                }}
              >
                <span style={{ flex: 1 }}>
                  {getMarketplaceCategoryLabel(t, cat.name)}
                </span>
                <span className={styles.categoryCount}>{cat.count}</span>
              </PageDropdownItem>
            ))}
            <span className={styles.filterMenuSection}>
              {t['com.streamlain.marketplace.browse.filter.installation']()}
            </span>
            {(['all', 'installed', 'not-installed'] as const).map(filter => (
              <PageDropdownItem
                key={filter}
                selected={installationFilter === filter}
                onClick={() => {
                  onInstallationFilterChange(filter);
                }}
              >
                {installationLabels[filter]}
              </PageDropdownItem>
            ))}
            <span className={styles.filterMenuSection}>
              {t['com.streamlain.marketplace.browse.filter.trust']()}
            </span>
            {(
              [
                'all',
                'streamlain_built',
                'streamlain_certified',
                'community_built',
              ] as const
            ).map(filter => (
              <PageDropdownItem
                key={filter}
                selected={provenanceFilter === filter}
                onClick={() => {
                  onProvenanceFilterChange(filter);
                }}
              >
                {provenanceLabels[filter]}
              </PageDropdownItem>
            ))}
            {activeFilterCount > 0 && (
              <PageDropdownItem
                selected={false}
                onClick={() => {
                  onResetFacetFilters();
                  close();
                }}
              >
                {t['com.streamlain.marketplace.browse.filter.reset']()}
              </PageDropdownItem>
            )}
          </>
        )}
      </PageDropdown>

      {showSort && (
        <PageDropdown
          trigger={
            <>
              <SortIcon size={14} />
              <PageDropdownLabel>
                {t['com.streamlain.marketplace.browse.sort.label']()}
              </PageDropdownLabel>
              {t[activeSortLabel as keyof typeof t]()}
            </>
          }
        >
          {({ close }) =>
            sortOptions.map(opt => (
              <PageDropdownItem
                key={opt.key}
                selected={sortBy === opt.key}
                onClick={() => {
                  onSortChange(opt.key);
                  close();
                }}
              >
                {t[opt.labelKey as keyof typeof t]()}
              </PageDropdownItem>
            ))
          }
        </PageDropdown>
      )}
      {resultCountText ? (
        <PageResultCount testId="marketplace-result-count">
          {resultCountText}
        </PageResultCount>
      ) : null}
    </PageFilterRow>
  );
}

function BrowseHero({
  total,
  featured,
  activeType,
  hideStats = false,
  scope,
}: {
  total: number;
  featured: number;
  activeType: BrowseTypeKey;
  hideStats?: boolean;
  scope: 'global' | 'workspace';
}) {
  const t = useI18n();
  const subtitleKey =
    `com.streamlain.marketplace.browse.hero.subtitle.${activeType}` as const;

  const stats: Array<{ label: string; value: string }> = [
    {
      label: t['com.streamlain.marketplace.browse.hero.stat.catalog'](),
      value: formatCount(total),
    },
  ];
  if (featured > 0) {
    stats.push({
      label: t['com.streamlain.marketplace.browse.hero.stat.featured'](),
      value: formatCount(featured),
    });
  }

  if (scope === 'workspace') {
    return (
      <PageHero
        eyebrow={t['com.streamlain.marketplace.workspace.hero.eyebrow']()}
        title={t['com.streamlain.marketplace.workspace.hero.title']()}
        subtitle={t['com.streamlain.marketplace.workspace.hero.subtitle']()}
        stats={
          hideStats
            ? []
            : [
                {
                  label:
                    t[
                      'com.streamlain.marketplace.workspace.hero.stat.templates'
                    ](),
                  value: formatCount(total),
                },
              ]
        }
      />
    );
  }

  return (
    <PageHero
      eyebrow={t['com.streamlain.marketplace.browse.hero.eyebrow']()}
      title={t['com.streamlain.marketplace.browse.hero.title']()}
      subtitle={t[subtitleKey]()}
      stats={hideStats ? [] : stats}
    />
  );
}

function SectionHeader({
  label,
  featured,
}: {
  label: string;
  featured?: boolean;
}) {
  return (
    <div className={styles.featuredSectionHeader}>
      <span className={styles.featuredSectionLabel}>
        {featured && <StarIcon size={11} />}
        {label}
      </span>
      <span className={styles.featuredSectionRule} aria-hidden />
    </div>
  );
}
